import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const MAX_MS = 120000;

export const MicButton = ({ auth, onText, testid }) => {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const timer = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("Voice typing isn't supported in this browser");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ["audio/webm", "audio/mp4", "audio/ogg"].find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearTimeout(timer.current);
        setState("busy");
        try {
          const blob = new Blob(chunks, { type: r.mimeType || "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, `voice.${(r.mimeType || "audio/webm").split("/")[1].split(";")[0]}`);
          const { data } = await api.post("/stt", fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
          if (data.text) onText(data.text); else toast.error("Didn't catch that — please try again");
        } catch { toast.error("Could not transcribe, please try again"); } finally { setState("idle"); }
      };
      rec.current = r;
      r.start();
      setState("rec");
      timer.current = setTimeout(() => r.state === "recording" && r.stop(), MAX_MS);
    } catch { toast.error("Please allow microphone access"); }
  };
  const stop = () => rec.current?.state === "recording" && rec.current.stop();

  return (
    <button type="button" onClick={state === "rec" ? stop : start} disabled={state === "busy"} data-testid={testid}
      title={state === "rec" ? "Stop & insert text" : "Speak (Hindi or English)"}
      className={`shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-bold border transition-colors ${state === "rec" ? "bg-red-600 text-white border-red-600 animate-pulse" : "bg-white text-[#0A192F] border-slate-300 hover:border-[#FF2E63] hover:text-[#FF2E63]"}`}>
      {state === "busy" ? <Loader2 className="w-4 h-4 animate-spin" /> : state === "rec" ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-4 h-4" />}
      {state === "busy" ? "Typing…" : state === "rec" ? "Stop" : "Speak"}
    </button>
  );
};

export const appendText = (prev, t) => (prev.trim() ? `${prev.trim()} ${t}` : t);
